import { Link2 } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { CARD_HEIGHT, CARD_WIDTH, cardTopY, type LayoutEntry } from './layout-events'

export interface TimelineCardEvent {
  id: string
  /** Display label for the card header. For edge events this is the relation
   *  type (e.g. `CONTACTED`); for node events this is the convention-derived
   *  event label (e.g. `Signed on`). */
  type: string
  /** Timestamp shown in the card header. For interval events this is the
   *  start; the end is shown alongside. */
  timestamp: string
  precision?: 'day' | 'instant'
  /** When defined, this event is an interval and the card displays a `start →
   *  end` range. */
  endTimestamp?: string
  /** Whether the event covers a span of time (interval) rather than a single
   *  instant (point). Drives both the header formatting and the duration bar
   *  drawn on the timeline axis. */
  isInterval?: boolean
  /** Primary participant — always present. */
  sourceId: string
  sourceName: string
  sourceGlyph: string
  /** Optional secondary participant. Edge-sourced events name both the source
   *  and target nodes; node-sourced events have only a primary participant. */
  targetId?: string
  targetName?: string
  targetGlyph?: string
}

export interface TimelineHoverPayload {
  kind: 'edge' | 'node'
  edgeId?: string
  nodeId?: string
  anchor: HTMLElement
}

interface TimelineEventCardProps {
  event: TimelineCardEvent
  layout: LayoutEntry
  axisY: number
  isSelected: boolean
  onSelectEdge: (edgeId: string, anchor: HTMLElement) => void
  onSelectNode: (nodeId: string, anchor: HTMLElement) => void
  onHoverEnter?: (payload: TimelineHoverPayload) => void
  onHoverLeave?: () => void
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function formatDateTime(ts: string): string {
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return ts
  const month = MONTHS[d.getMonth()]
  const day = String(d.getDate()).padStart(2, '0')
  const year = d.getFullYear()
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${month} ${day}, ${year} · ${hh}:${mm}`
}

function formatDateOnly(ts: string, utc = false): string {
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return ts
  return utc
    ? `${MONTHS[d.getUTCMonth()]} ${String(d.getUTCDate()).padStart(2, '0')}, ${d.getUTCFullYear()}`
    : `${MONTHS[d.getMonth()]} ${String(d.getDate()).padStart(2, '0')}, ${d.getFullYear()}`
}

export function eventTimestampLabel(event: TimelineCardEvent): string {
  const format =
    event.precision === 'day'
      ? (value: string) => formatDateOnly(value, true)
      : event.precision === 'instant' || !event.isInterval
        ? formatDateTime
        : formatDateOnly
  return event.isInterval && event.endTimestamp
    ? `${format(event.timestamp)} → ${format(event.endTimestamp)}`
    : format(event.timestamp)
}

export function TimelineEventCard({
  event,
  layout,
  axisY,
  isSelected,
  onSelectEdge,
  onSelectNode,
  onHoverEnter,
  onHoverLeave,
}: TimelineEventCardProps) {
  const top = cardTopY(axisY, layout)
  const left = layout.x - CARD_WIDTH / 2

  const headerStamp = eventTimestampLabel(event)

  return (
    <Card
      asChild
      size="compact"
      interactive
      selected={isSelected}
      className={isSelected ? '' : 'shadow-sm'}
    >
      <button
        type="button"
        data-event-card
        onMouseDown={e => e.stopPropagation()}
        onClick={e => onSelectEdge(event.id, e.currentTarget)}
        onMouseEnter={e =>
          onHoverEnter?.({ kind: 'edge', edgeId: event.id, anchor: e.currentTarget })
        }
        onMouseLeave={() => onHoverLeave?.()}
        className="absolute block text-left p-0 gap-0"
        style={{
          left,
          top,
          width: CARD_WIDTH,
          height: CARD_HEIGHT,
        }}
      >
        <div className="flex items-center justify-between gap-2 border-b border-border/60 bg-muted/40 px-2 py-1">
          <div className="flex min-w-0 items-center gap-1.5">
            <Link2 className="size-3 shrink-0 text-primary" />
            <span className="truncate text-3xs font-medium uppercase tracking-wider text-primary">
              {event.type.replace(/_/g, ' ')}
            </span>
          </div>
          <span className="shrink-0 font-mono text-3xs text-muted-foreground tabular-nums">
            {headerStamp}
          </span>
        </div>
        <div className="px-2 py-1">
          <EntityLine
            glyph={event.sourceGlyph}
            name={event.sourceName}
            onClick={e => {
              e.stopPropagation()
              onSelectNode(event.sourceId, e.currentTarget)
            }}
            onHoverEnter={e =>
              onHoverEnter?.({ kind: 'node', nodeId: event.sourceId, anchor: e.currentTarget })
            }
            onHoverLeave={() => onHoverLeave?.()}
          />
          {event.targetId && event.targetName ? (
            <EntityLine
              glyph={event.targetGlyph ?? '???'}
              name={event.targetName}
              onClick={e => {
                e.stopPropagation()
                onSelectNode(event.targetId as string, e.currentTarget)
              }}
              onHoverEnter={e =>
                onHoverEnter?.({
                  kind: 'node',
                  nodeId: event.targetId as string,
                  anchor: e.currentTarget,
                })
              }
              onHoverLeave={() => onHoverLeave?.()}
            />
          ) : null}
        </div>
      </button>
    </Card>
  )
}

interface EntityLineProps {
  glyph: string
  name: string
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void
  onHoverEnter?: (e: React.MouseEvent<HTMLButtonElement>) => void
  onHoverLeave?: () => void
}

function EntityLine({ glyph, name, onClick, onHoverEnter, onHoverLeave }: EntityLineProps) {
  return (
    <div className="flex items-center gap-1.5 leading-tight">
      <span className="inline-flex h-4 w-7 shrink-0 items-center justify-center rounded-sm border border-border/60 bg-background/60 font-mono text-[8.5px] font-semibold uppercase tracking-wider text-muted-foreground">
        {glyph}
      </span>
      <button
        type="button"
        onMouseDown={e => e.stopPropagation()}
        onClick={onClick}
        onMouseEnter={e => {
          e.stopPropagation()
          onHoverEnter?.(e)
        }}
        onMouseLeave={onHoverLeave}
        className="truncate text-xs text-foreground hover:text-primary hover:underline decoration-dotted underline-offset-2 cursor-pointer max-md:-my-1 max-md:py-1"
      >
        {name}
      </button>
    </div>
  )
}
